// This file contains JSX templates for the Anmodning popup contents.
import React, { useState } from 'react';
import { mapTranslations } from '../libs/parseVendorData';
import './Anmodning._PopupContents.css';
import { convertQuickFilterV7ToLegacy } from '@mui/x-data-grid/internals';
import { CollectionsBookmarkRounded, Edit } from '@material-ui/icons';
import { IconButton, Tooltip } from '@material-ui/core';
import EditFeatureModal from './EditFeatureModal';
import { Auth, API } from "aws-amplify";

function prepareProperties(properties) {
    // The properties needs to be wrapped in a geojson feature for parsing
    // the properties needs to be converted from json strings to objects first
    const parsedProperties = {};
    for (const key in properties) {
        try {
            parsedProperties[key] = JSON.parse(properties[key]);
        } catch {
            parsedProperties[key] = properties[key];
        }
    }
    return { type: "Feature", properties: parsedProperties, geometry: null };
}

function categorizeProperties(mapping) {
    const unused = [];
    const used = [];
    
    for (const key in mapping) {
        if (mapping[key].translateInto === null) {
            unused.push(mapping[key]);
        } else {
            used.push(mapping[key]);
        }
    }
    
    return { unused, used };
}

function sortReplaceOptions(entries) {
    return entries.sort(([keyA], [keyB]) => {
        if (keyA === '_') return 1;
        if (keyB === '_') return -1;
        return 0;
    });
}

function shouldHighlightOption(key, originalValue, replaceOptions) {
    const hasExactMatch = Object.keys(replaceOptions).some(k => k !== '_' && k === originalValue);
    return key === originalValue || (key === '_' && !hasExactMatch);
}

function getItemClassName(shouldHighlight) {
    return shouldHighlight ? 'popup-replace-item-highlighted' : 'popup-replace-item';
}

function ReplaceOptionsDetails({ item, size = 'normal' }) {
    if (!item.replaceOptions) return null;
    
    const isSmall = size === 'small';
    const summary = isSmall 
        ? `Erstattet fra: "${item.originalValue}"`
        : '↳ Har mapping defineret';
    
    return (
        <details className={isSmall ? '' : 'popup-nested-details'}>
            <summary>{summary}</summary>
            <div className="popup-replace-details">
                {!isSmall && <strong>Mulige værdier:</strong>}
                <ReplaceOptionsList 
                    replaceOptions={item.replaceOptions}
                    originalValue={item.originalValue}
                    size={isSmall ? 'small' : 'normal'}
                />
            </div>
        </details>
    );
}

function PropertyTableRow({ item, showTranslation = false }) {
    const displayOriginal = item.original === null ? '_default' : item.original;
    
    return (
        <tr>
            <td className="popup-table-cell-label">
                {showTranslation ? (
                    <>
                        {item.translateInto}
                        <div className="popup-original-field">({displayOriginal})</div>
                    </>
                ) : (
                    `${displayOriginal}:`
                )}
            </td>
            <td className="popup-table-cell-value">
                {showTranslation ? <PropertyValue item={item} /> : item.originalValue}
                <ReplaceOptionsDetails item={item} size={showTranslation ? 'small' : 'normal'} />
            </td>
        </tr>
    );
}

function ReplaceOptionsList({ replaceOptions, originalValue, size = 'normal' }) {
    const sortedEntries = sortReplaceOptions(Object.entries(replaceOptions));
    const listClassName = size === 'small' ? 'popup-replace-list' : 'popup-nested-value-list';
    
    return (
        <ul className={listClassName}>
            {sortedEntries.map(([key, value]) => {
                const highlighted = shouldHighlightOption(key, originalValue, replaceOptions);
                const fallbackLabel = size === 'small' ? '(øvrige)' : '(fallback)';
                
                return (
                    <li key={key} className={getItemClassName(highlighted)}>
                        {key === '_' ? fallbackLabel : key} → {value}
                    </li>
                );
            })}
        </ul>
    );
}

function PropertyValue({ item }) {
    if (item.translateInto === 'ler:sti') {
        return (
            <a href={item.translatedValue} target="_blank" rel="noopener noreferrer" className="popup-link">
                {item.translatedValue}
            </a>
        );
    }
    return <>{item.translatedValue}</>;
}

function TranslatedPropertiesTable({ properties }) {
    if (properties.length === 0) {
        return <p className="popup-no-data">Ingen egenskaber at vise</p>;
    }
    
    return (
        <table className="popup-table">
            <tbody>
                {properties.map((item, index) => (
                    <PropertyTableRow key={index} item={item} showTranslation={true} />
                ))}
            </tbody>
        </table>
    );
}

function UnusedPropertiesTable({ properties }) {
    return (
        <table className="popup-table-small">
            <tbody>
                {properties.map((item, index) => (
                    <PropertyTableRow key={index} item={item} showTranslation={false} />
                ))}
            </tbody>
        </table>
    );
}

function ErrorMessageTable({ tests }) {
    return (
        <table className="popup-table-small">
            <tbody>
                {tests.map((msg, index) => (
                    <ErrorTableRow 
                        key={index}
                        item={{ original: msg.attribute, originalValue: msg.message }}
                    />
                ))}
            </tbody>
        </table>
    );
}

function GenericPropertiesTable({ properties }) {
    // Simple table without translations or replace options, just key-value pairs
    return (
        <table className="popup-table-small">
            <tbody>
                {Object.keys(properties).map(key => (
                    <GenericTableRow 
                        key={key}
                        item={{ original: key, originalValue: properties[key] }}
                    />
                ))}
            </tbody>
        </table>
    );
}

function GenericTableRow({ item }) {
    return (
        <tr>
            <td className="popup-table-cell-label">
                {item.original}
            </td>
            <td className="popup-table-cell-value">
                {item.originalValue}
            </td>
        </tr>
    );
}

function ErrorTableRow({ item }) {
    return (
        <tr>
            <td colSpan="2" className="popup-table-cell-label" style={{color: '#f44336'}}>
                {item.original}:
                <br />
                <span className="popup-original-error">
                    {item.originalValue}
                </span>
            </td>
        </tr>
    );
}

// Wrapper component for LER features
function LERPopupComponent({ properties, anmodId }) {
    const [editModalOpen, setEditModalOpen] = useState(false);
    const props = prepareProperties(properties);
    const mapped = mapTranslations(props);

    const pp = props.properties.properties;
    const pt = props.properties.config.transform;
    const pe = props.properties.error_msg;
    const { feature: mf, transform: mt, mapping: mm } = mapped;
    const { unused: unusedProperties, used: usedProperties } = categorizeProperties(mm);
    const title = pt["elementType"];

    const handleEditClick = (e) => {
        e.stopPropagation();
        setEditModalOpen(true);
    };

    const handleSaveFeature = async (anmodId) => {
        console.log('resetting:', anmodId);
        // The parent component could handle refetching data here
        try {
            const init = {
                headers: { Authorization: `Bearer ${(await Auth.currentSession()).getIdToken().getJwtToken()}` }
            };
            await API.post("API", "/Anmodning/restart/" + anmodId + "/transform/false", init);
        }
        catch (error) {
            console.error('Error restarting anmodning:', error);
        }
    };

    return (
        <div>
            <details className="popup-featuretype-title">
                <summary>
                    <span className="popup-summary-text">{title}</span>
                    <Tooltip title="Rediger feature">
                        <IconButton 
                            size="small" 
                            onClick={handleEditClick}
                            className="popup-edit-button-inline"
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </summary>
                <span className="popup-caption">
                    {mf.ident_col} = {pp[mf.ident_col]} i {mf.featuretype}
                </span>

                {pe && pe.tests && pe.tests.length > 0 && (
                    <ErrorMessageTable tests={pe.tests} />
                )}

                <details className="popup-details-section">
                    <summary>Oversatte felter ({usedProperties.length})</summary>
                    <TranslatedPropertiesTable properties={usedProperties} />
                </details>

                <details className="popup-details-section">
                    <summary>Øvrige felter ({unusedProperties.length})</summary>
                    <UnusedPropertiesTable properties={unusedProperties} />
                </details>
            </details>

            <EditFeatureModal
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                feature={{ properties: props.properties }}
                anmodId={anmodId}
                onSave={handleSaveFeature}
            />
        </div>
    );
}

export const LERPopupContents = (properties, anmodId) => {
    return <LERPopupComponent properties={properties} anmodId={anmodId} />;
};

// Wrapper component for Other features
function OtherPopupComponent({ properties, anmodId }) {
    const [editModalOpen, setEditModalOpen] = useState(false);
    const props = prepareProperties(properties);
    const pp = props.properties.properties;
    const pf = props.properties.config.feature;
    const pc = props.properties.config;
    
    console.log(props);
    var title = "Ikke-implementeret popup";

    if (pc.is_manuel) {
        title = "Område til manuel behandling";
    }

    const handleEditClick = (e) => {
        e.stopPropagation();
        setEditModalOpen(true);
    };

    return (
        <div>
            <details className="popup-featuretype-title">
                <summary>
                    <span className="popup-summary-text">{title}</span>
                    <Tooltip title="Rediger feature">
                        <IconButton 
                            size="small" 
                            onClick={handleEditClick}
                            className="popup-edit-button-inline"
                        >
                            <Edit fontSize="small" />
                        </IconButton>
                    </Tooltip>
                </summary>
                <span className="popup-caption">
                    {pf.ident_col} = {pp[pf.ident_col]} i {pf.featuretype}
                </span>

                <details className="popup-details-section">
                    <summary>Felter ({Object.keys(pp).length})</summary>
                    <GenericPropertiesTable properties={pp} />
                </details>
            </details>

            <EditFeatureModal
                open={editModalOpen}
                onClose={() => setEditModalOpen(false)}
                feature={{ properties: props.properties }}
                anmodId={anmodId}
            />
        </div>
    );
}

export const OtherPopupContents = (properties, anmodId) => {
    return <OtherPopupComponent properties={properties} anmodId={anmodId} />;
};